import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { getFullDateWithOrdinals } from '../../../Utils/UtilFunctions';
import { GoldRates } from '../Rates/GoldRates';
import { Select } from '../Select';
import { GOLD_DATA, METALS } from '../types';
import { checkNumberSign } from '../utils';

interface CaratChange {
  change22k: number;
  change24k: number;
}

interface RATE_CARD_PROPS {
  data: GOLD_DATA;
  cities: string[];
  selectedCity: string;
  change?: CaratChange;
  percentageChange?: CaratChange;
}

const GoldRateCard = ({
  data,
  cities,
  selectedCity,
  change = { change22k: 0, change24k: 0 },
  percentageChange = { change22k: 0, change24k: 0 },
}: RATE_CARD_PROPS): JSX.Element => {
  const [goldData, setGoldData] = useState(data);
  const [gram, setGram] = useState('1');

  const gramList = [
    { label: '₹/1 g', value: '1' },
    { label: '₹/10 g', value: '10' },
  ];

  const image1 =
    'https://assets1.cleartax-cdn.com/cleartax/images/1676357921_24carat.png';
  const image2 =
    'https://assets1.cleartax-cdn.com/cleartax/images/1676357930_22carat.png';

  const router = useRouter();

  const onCityChange = (val: string) => {
    router.push(`/${METALS.GOLD}-rate-${val}`);
  };

  const onGramChange = (val: string) => {
    const value = Number(val);
    const today: GOLD_DATA = {
      ...data,
      carat24: {
        ratePerGram: Number(data.carat24.ratePerGram) * value,
        change: data.carat24.change * value,
      },
      carat22: {
        ratePerGram: Number(data.carat22.ratePerGram) * value,
        change: data.carat22.change * value,
      },
    };
    setGoldData(today);
    setGram(val);
  };

  return (
    <div className="bg-[#FCFAFF] p-6 border border-[#EBEBEC] rounded-md sm:p-4">
      <div className="flex justify-between gap-4 sm:flex-col-reverse">
        <div>
          <h1 className="text-gray-700 font-bold text-s-20 sm:text-center">
            {`Today's Gold Rate in `}
            <span className="capitalize">{selectedCity}</span>
          </h1>
          <div className="text-s-14 text-font-200 sm:text-center">
            {getFullDateWithOrdinals(new Date().toDateString())}
          </div>
        </div>
        <div className="flex gap-4 items-start">
          <Select
            data={cities}
            onChangeHandler={onCityChange}
            selected={selectedCity}
            id="cities"
          />
          <Select data={gramList} onChangeHandler={onGramChange} id="gram" />
        </div>
      </div>

      <div className="flex justify-between gap-2 items-center pt-4">
        <div className="w-1/2">
          <GoldRates
            image={image1}
            rate={goldData.carat24.ratePerGram}
            changeSign={checkNumberSign(change?.change24k)}
            percentageChangeSign={checkNumberSign(percentageChange?.change24k)}
            gram={gram}
            change={gram === '1' ? change?.change24k / 10 : change?.change24k}
            percentageChange={percentageChange?.change24k}
          />
        </div>
        <div className="w-1/2">
          <GoldRates
            image={image2}
            rate={goldData.carat22.ratePerGram}
            changeSign={checkNumberSign(change?.change22k)}
            percentageChangeSign={checkNumberSign(percentageChange?.change22k)}
            gram={gram}
            change={gram === '1' ? change?.change22k / 10 : change?.change22k}
            percentageChange={percentageChange?.change22k}
          />
        </div>
      </div>
    </div>
  );
};

export { GoldRateCard };
